// Shared staff types, lookups, and seed data used by Staff Management and HR Admin.

export type StaffStatus    = 'Active' | 'Inactive' | 'On Leave' | 'Resigned' | 'Terminated';
export type StaffType      = 'Teaching' | 'Non-Teaching' | 'Administrative' | 'Support';
export type StaffGender    = 'Male' | 'Female' | 'Other';
export type EmploymentType = 'Permanent' | 'Contractual' | 'Part-time' | 'Guest';

export interface Qualification {
  degree:        string;
  subject:       string;
  institution:   string;
  yearOfPassing: number | null;
  percentage:    number | null;
}

export interface StaffDocument {
  name:     string;
  fileName: string;
  file:     File | null;
}

export interface StaffMember {
  id:               number;
  employeeId:       string;
  firstName:        string;
  lastName:         string;
  gender:           StaffGender;
  dateOfBirth:      string;
  bloodGroup:       string;
  nationality:      string;
  religion:         string;
  aadhaarNumber:    string;
  panNumber:        string;
  mobile:           string;
  alternateMobile:  string;
  email:            string;
  address:          string;
  city:             string;
  state:            string;
  pinCode:          string;
  staffType:        StaffType;
  department:       string;
  designation:      string;
  employmentType:   EmploymentType;
  dateOfJoining:    string;
  dateOfLeaving:    string;
  experience:       number | null;
  subjects:         string[];
  qualifications:   Qualification[];
  basicSalary:      number | null;
  status:           StaffStatus;
  profilePhoto:     string;
  documents:        StaffDocument[];
  remarks:          string;
  createdAt:        string;
  updatedAt:        string;
}

export const STAFF_TYPES: StaffType[] = ['Teaching', 'Non-Teaching', 'Administrative', 'Support'];
export const EMPLOYMENT_TYPES: EmploymentType[] = ['Permanent', 'Contractual', 'Part-time', 'Guest'];
export const STAFF_STATUSES: StaffStatus[] = ['Active', 'Inactive', 'On Leave', 'Resigned', 'Terminated'];
export const STAFF_GENDERS: StaffGender[] = ['Male', 'Female', 'Other'];

export const STAFF_BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
export const STAFF_RELIGIONS    = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Other'];
export const STAFF_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana',
  'Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya',
  'Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
  'Uttar Pradesh','Uttarakhand','West Bengal','Other',
];

export const STAFF_DEPARTMENTS = [
  'Science', 'Mathematics', 'English', 'Hindi', 'Social Science',
  'Commerce', 'Arts', 'Computer Science', 'Physical Education',
  'Administration', 'Accounts & Finance', 'Library', 'Laboratory',
  'Hostel', 'Transport', 'Sports', 'Music & Arts', 'Other',
];

export const STAFF_DESIGNATIONS: Record<StaffType, string[]> = {
  'Teaching':       ['Principal', 'Vice Principal', 'Head of Department', 'Senior Teacher', 'Teacher', 'Assistant Teacher', 'Guest Faculty', 'Lecturer', 'Professor', 'Associate Professor'],
  'Non-Teaching':   ['Librarian', 'Lab Assistant', 'Lab Technician', 'Computer Operator', 'Clerk', 'Accountant', 'Cashier', 'Receptionist', 'Counsellor'],
  'Administrative': ['Administrator', 'Deputy Administrator', 'HR Manager', 'Office Superintendent', 'Office Assistant', 'Data Entry Operator'],
  'Support':        ['Peon', 'Guard / Security', 'Gardener', 'Sweeper', 'Cook', 'Driver', 'Bus Attendant', 'Electrician', 'Plumber'],
};

export const STAFF_SUBJECTS = [
  'English', 'Hindi', 'Mathematics', 'Science', 'Physics', 'Chemistry', 'Biology',
  'Social Science', 'History', 'Geography', 'Civics', 'Economics',
  'Commerce', 'Accountancy', 'Business Studies', 'Computer Science',
  'Physical Education', 'Music', 'Art & Craft', 'Sanskrit', 'French', 'Other',
];

export const DEFAULT_STAFF_DOCUMENTS: StaffDocument[] = [
  { name: 'Aadhaar Card',               fileName: '', file: null },
  { name: 'PAN Card',                   fileName: '', file: null },
  { name: 'Qualification Certificate',  fileName: '', file: null },
  { name: 'Experience Certificate',     fileName: '', file: null },
  { name: 'Passport Photo',             fileName: '', file: null },
];

const EMP_ID_PREFIX: Record<StaffType, string> = {
  Teaching: 'TCH', 'Non-Teaching': 'NTC', Administrative: 'ADM', Support: 'SUP',
};

export function staffFullName(s: Pick<StaffMember, 'firstName' | 'lastName'>): string {
  return [s.firstName, s.lastName].filter(Boolean).join(' ');
}

export function generateStaffEmpId(staffType: StaffType, id: number, year = 2025): string {
  return `${EMP_ID_PREFIX[staffType] ?? 'EMP'}-${year}-${String(id).padStart(4, '0')}`;
}

export function blankStaffMember(): StaffMember {
  return {
    id: 0, employeeId: '',
    firstName: '', lastName: '', gender: 'Male', dateOfBirth: '', bloodGroup: '',
    nationality: 'Indian', religion: '', aadhaarNumber: '', panNumber: '',
    mobile: '', alternateMobile: '', email: '',
    address: '', city: '', state: '', pinCode: '',
    staffType: 'Teaching', department: '', designation: '', employmentType: 'Permanent',
    dateOfJoining: '', dateOfLeaving: '', experience: null, subjects: [],
    qualifications: [{ degree: '', subject: '', institution: '', yearOfPassing: null, percentage: null }],
    basicSalary: null,
    status: 'Active', profilePhoto: '',
    documents: DEFAULT_STAFF_DOCUMENTS.map(d => ({ ...d })),
    remarks: '', createdAt: '', updatedAt: '',
  };
}

export function generateStaffMockData(): StaffMember[] {
  const data: Partial<StaffMember>[] = [
    { firstName:'Rajesh', lastName:'Kumar',     gender:'Male',   staffType:'Teaching',        department:'Mathematics',        designation:'Head of Department', employmentType:'Permanent',   dateOfJoining:'2015-06-01', experience:12, mobile:'9876543210', email:'rajesh.kumar@edusmart.in',     status:'Active',   basicSalary:55000, subjects:['Mathematics'] },
    { firstName:'Priya',  lastName:'Sharma',    gender:'Female', staffType:'Teaching',        department:'Science',            designation:'Senior Teacher',     employmentType:'Permanent',   dateOfJoining:'2017-08-15', experience:9,  mobile:'9845123456', email:'priya.sharma@edusmart.in',     status:'Active',   basicSalary:48000, subjects:['Physics','Chemistry'] },
    { firstName:'Amit',   lastName:'Verma',     gender:'Male',   staffType:'Teaching',        department:'English',            designation:'Teacher',            employmentType:'Permanent',   dateOfJoining:'2019-04-01', experience:7,  mobile:'9812345678', email:'amit.verma@edusmart.in',       status:'Active',   basicSalary:42000, subjects:['English'] },
    { firstName:'Sunita', lastName:'Singh',     gender:'Female', staffType:'Administrative',  department:'Administration',     designation:'Administrator',      employmentType:'Permanent',   dateOfJoining:'2012-03-01', experience:14, mobile:'9867543210', email:'sunita.singh@edusmart.in',     status:'Active',   basicSalary:60000 },
    { firstName:'Vikram', lastName:'Patel',     gender:'Male',   staffType:'Non-Teaching',    department:'Library',            designation:'Librarian',          employmentType:'Permanent',   dateOfJoining:'2016-07-01', experience:10, mobile:'9856712345', email:'vikram.patel@edusmart.in',     status:'Active',   basicSalary:35000 },
    { firstName:'Meena',  lastName:'Joshi',     gender:'Female', staffType:'Teaching',        department:'Hindi',              designation:'Teacher',            employmentType:'Permanent',   dateOfJoining:'2020-06-01', experience:5,  mobile:'9823456789', email:'meena.joshi@edusmart.in',      status:'Active',   basicSalary:40000, subjects:['Hindi','Sanskrit'] },
    { firstName:'Suresh', lastName:'Agarwal',   gender:'Male',   staffType:'Teaching',        department:'Computer Science',   designation:'Senior Teacher',     employmentType:'Contractual', dateOfJoining:'2021-01-15', experience:4,  mobile:'9834567890', email:'suresh.agarwal@edusmart.in',   status:'Active',   basicSalary:38000, subjects:['Computer Science'] },
    { firstName:'Kavita', lastName:'Nair',      gender:'Female', staffType:'Administrative',  department:'Accounts & Finance', designation:'Accountant',         employmentType:'Permanent',   dateOfJoining:'2014-09-01', experience:13, mobile:'9845678901', email:'kavita.nair@edusmart.in',      status:'Active',   basicSalary:45000 },
    { firstName:'Rahul',  lastName:'Mishra',    gender:'Male',   staffType:'Teaching',        department:'Social Science',     designation:'Teacher',            employmentType:'Permanent',   dateOfJoining:'2018-04-01', experience:8,  mobile:'9867890123', email:'rahul.mishra@edusmart.in',     status:'On Leave', basicSalary:41000, subjects:['History','Geography'] },
    { firstName:'Anjali', lastName:'Gupta',     gender:'Female', staffType:'Teaching',        department:'Mathematics',        designation:'Teacher',            employmentType:'Part-time',   dateOfJoining:'2022-06-01', experience:3,  mobile:'9878901234', email:'anjali.gupta@edusmart.in',     status:'Active',   basicSalary:28000, subjects:['Mathematics'] },
    { firstName:'Deepak', lastName:'Yadav',     gender:'Male',   staffType:'Support',         department:'Transport',          designation:'Driver',             employmentType:'Permanent',   dateOfJoining:'2013-01-01', experience:13, mobile:'9889012345', email:'deepak.yadav@edusmart.in',     status:'Active',   basicSalary:22000 },
    { firstName:'Pooja',  lastName:'Malhotra',  gender:'Female', staffType:'Non-Teaching',    department:'Laboratory',         designation:'Lab Assistant',      employmentType:'Permanent',   dateOfJoining:'2019-07-01', experience:7,  mobile:'9890123456', email:'pooja.malhotra@edusmart.in',   status:'Active',   basicSalary:30000 },
    { firstName:'Arjun',  lastName:'Chauhan',   gender:'Male',   staffType:'Teaching',        department:'Physical Education', designation:'Teacher',            employmentType:'Permanent',   dateOfJoining:'2017-06-01', experience:9,  mobile:'9801234567', email:'arjun.chauhan@edusmart.in',    status:'Active',   basicSalary:39000, subjects:['Physical Education'] },
    { firstName:'Neha',   lastName:'Kapoor',    gender:'Female', staffType:'Administrative',  department:'Administration',     designation:'Office Assistant',   employmentType:'Permanent',   dateOfJoining:'2020-09-01', experience:6,  mobile:'9812345609', email:'neha.kapoor@edusmart.in',      status:'Active',   basicSalary:32000 },
    { firstName:'Manish', lastName:'Tiwari',    gender:'Male',   staffType:'Teaching',        department:'Commerce',           designation:'Lecturer',           employmentType:'Guest',       dateOfJoining:'2023-01-01', experience:2,  mobile:'9823456700', email:'manish.tiwari@edusmart.in',    status:'Active',   basicSalary:25000, subjects:['Accountancy','Business Studies'] },
    { firstName:'Ritu',   lastName:'Saxena',    gender:'Female', staffType:'Teaching',        department:'Arts',               designation:'Teacher',            employmentType:'Permanent',   dateOfJoining:'2016-03-01', experience:10, mobile:'9834567801', email:'ritu.saxena@edusmart.in',      status:'Inactive', basicSalary:36000, subjects:['Art & Craft','Music'] },
    { firstName:'Sanjay', lastName:'Dubey',     gender:'Male',   staffType:'Non-Teaching',    department:'Administration',     designation:'Computer Operator',  employmentType:'Contractual', dateOfJoining:'2021-06-01', experience:5,  mobile:'9845678902', email:'sanjay.dubey@edusmart.in',     status:'Active',   basicSalary:28000 },
    { firstName:'Shweta', lastName:'Pandey',    gender:'Female', staffType:'Teaching',        department:'Science',            designation:'Teacher',            employmentType:'Permanent',   dateOfJoining:'2018-06-01', experience:8,  mobile:'9856712346', email:'shweta.pandey@edusmart.in',    status:'Active',   basicSalary:43000, subjects:['Biology'] },
    { firstName:'Mohit',  lastName:'Sharma',    gender:'Male',   staffType:'Support',         department:'Hostel',             designation:'Guard / Security',   employmentType:'Permanent',   dateOfJoining:'2010-04-01', experience:16, mobile:'9867543211', email:'mohit.sharma@edusmart.in',     status:'Active',   basicSalary:18000 },
    { firstName:'Asha',   lastName:'Srivastava',gender:'Female', staffType:'Administrative',  department:'Accounts & Finance', designation:'Cashier',            employmentType:'Permanent',   dateOfJoining:'2015-11-01', experience:11, mobile:'9878901235', email:'asha.srivastava@edusmart.in',  status:'Active',   basicSalary:38000 },
  ];

  return data.map((d, i) => ({
    id:             i + 1,
    employeeId:     generateStaffEmpId(d.staffType!, i + 1),
    firstName:      d.firstName!,
    lastName:       d.lastName!,
    gender:         d.gender ?? 'Male',
    dateOfBirth:    `${1975 + (i % 15)}-${String((i % 12) + 1).padStart(2, '0')}-${String((i % 28) + 1).padStart(2, '0')}`,
    bloodGroup:     ['A+', 'B+', 'O+', 'AB+', 'A-', 'B-', 'O-'][i % 7],
    nationality:    'Indian',
    religion:       ['Hindu', 'Muslim', 'Christian', 'Sikh'][i % 4],
    aadhaarNumber:  `${5000 + i}${String(i * 7 + 12345).padStart(8, '0')}`,
    panNumber:      `ABCDE${1000 + i}F`,
    mobile:         d.mobile!,
    alternateMobile:'',
    email:          d.email!,
    address:        `${10 + i}, Sector ${i + 1}`,
    city:           ['Delhi', 'Mumbai', 'Pune', 'Jaipur', 'Lucknow', 'Bhopal'][i % 6],
    state:          ['Delhi', 'Maharashtra', 'Rajasthan', 'Uttar Pradesh', 'Madhya Pradesh'][i % 5],
    pinCode:        `1100${String(i).padStart(2, '0')}`,
    staffType:      d.staffType!,
    department:     d.department!,
    designation:    d.designation!,
    employmentType: d.employmentType!,
    dateOfJoining:  d.dateOfJoining!,
    dateOfLeaving:  '',
    experience:     d.experience ?? 1,
    subjects:       d.subjects ?? [],
    qualifications: [{ degree: 'B.Ed', subject: d.subjects?.[0] ?? 'General', institution: 'Delhi University', yearOfPassing: 2010 + (i % 10), percentage: 70 + (i % 15) }],
    basicSalary:    d.basicSalary ?? 30000,
    status:         d.status!,
    profilePhoto:   '',
    documents:      [
      { name: 'Aadhaar Card',             fileName: `aadhaar_${i + 1}.pdf`, file: null },
      { name: 'Qualification Certificate', fileName: `qual_${i + 1}.pdf`,    file: null },
      { name: 'Passport Photo',            fileName: `photo_${i + 1}.jpg`,   file: null },
    ],
    remarks:        '',
    createdAt:      new Date(2023, i % 12, (i % 28) + 1).toISOString(),
    updatedAt:      new Date().toISOString(),
  }));
}
